import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, Play } from 'lucide-react';
import HeroNetwork from './HeroNetwork';

const Hero: React.FC = () => {
  const prefersReducedMotion = useReducedMotion();

  const easeOut = [0.22, 1, 0.36, 1] as const;

  const fadeUp = (delay = 0) =>
    prefersReducedMotion
      ? {}
      : {
          initial: { opacity: 0, y: 24 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.65, ease: easeOut, delay },
        };

  return (
    <section id="home" className="relative min-h-screen bg-secondary-900 overflow-hidden pt-28">
      {/* Atmosphere */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-secondary-900 via-[#0f0f0f] to-secondary-900" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[90vw] max-w-5xl h-[45vh] bg-accent-500/[0.07] blur-[110px] rounded-full" />
        <div className="absolute bottom-0 right-0 w-[35vw] h-[35vh] bg-accent-600/[0.04] blur-[90px] rounded-full" />
      </div>

      {/* Orange waves + traveling pulses + rings */}
      <div className="absolute inset-0 opacity-90">
        <HeroNetwork />
      </div>

      {/* Soft vignette so text stays readable */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(11,11,11,0.55)_70%,rgba(11,11,11,0.85)_100%)]" />

      <div className="container-custom relative z-10 py-16 md:py-24">
        <div className="text-center max-w-4xl mx-auto">
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 bg-accent-500/10 backdrop-blur-sm text-accent-400 rounded-full text-xs font-medium mb-8 border border-accent-500/25 tracking-[0.14em] uppercase"
            {...fadeUp(0.05)}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse" />
            Cloud Computing & DevSecOps
          </motion.div>

          <motion.h1
            className="text-5xl md:text-6xl lg:text-7xl font-display font-black text-white mb-6 leading-[0.95] tracking-tight"
            {...fadeUp(0.12)}
          >
            Hybrid Bee Technology
          </motion.h1>

          <motion.p
            className="text-lg md:text-xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed"
            {...fadeUp(0.22)}
          >
            Somos una empresa especializada en ofrecer soluciones avanzadas de Cloud Computing
            y DevSecOps. Nuestro objetivo principal es ayudar a las empresas a transformar,
            automatizar y optimizar su infraestructura tecnológica mediante enfoques ágiles
            y seguros.
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center mb-16"
            {...fadeUp(0.32)}
          >
            <a
              href="#contact"
              className="btn-primary py-4 px-8 text-lg inline-flex items-center justify-center"
            >
              Contactanos
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>

            <a
              href="#services"
              className="btn-outline py-4 px-8 text-lg inline-flex items-center justify-center"
            >
              <Play className="w-5 h-5 mr-2" />
              Ver Servicios
            </a>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto"
            {...fadeUp(0.42)}
          >
            {[
              { value: '55+', label: 'Clientes Satisfechos' },
              { value: '19+', label: 'Años de Experiencia' },
              { value: '20+', label: 'Proyectos Exitosos' },
            ].map((stat) => (
              <div
                key={stat.label}
                className="bg-surface-raised/60 backdrop-blur-sm rounded-2xl px-5 py-5 border border-border/80 card-hover"
              >
                <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-accent-400/90 text-xs tracking-[0.12em] uppercase">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <div className="w-6 h-10 border border-accent-500/40 rounded-full flex justify-center">
          <div className="w-1 h-2.5 bg-accent-500/70 rounded-full mt-2 animate-scroll-hint" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
